import React, { useEffect, useRef, useState } from 'react';
import { Modal, Button, Message, Loader } from 'semantic-ui-react';
import { useComponentManager } from '../../providers/ComponentManagerProvider';
import { componentLabel } from './componentLabel';

export const MovePointModal = ({ open, setOpen, point, currentComponentId, onMoved }) => {
    const { fetchComponentRoots, fetchComponentChildren, moveComponentPoint, getPointDependents } =
        useComponentManager();

    const [columns, setColumns] = useState([]);
    const [path, setPath] = useState([]);
    const [target, setTarget] = useState(null);
    const [loadingColumn, setLoadingColumn] = useState(null);
    const [treeError, setTreeError] = useState(false);

    const [dependentCount, setDependentCount] = useState(null);
    const [moving, setMoving] = useState(false);
    const [error, setError] = useState(null);

    const scrollRef = useRef(null);
    const requestToken = useRef(0);
    const wasOpen = useRef(false);

    useEffect(() => {
        if (open && !wasOpen.current) {
            setColumns([]);
            setPath([]);
            setTarget(null);
            setLoadingColumn(null);
            setTreeError(false);
            setDependentCount(null);
            setError(null);

            fetchComponentRoots()
                .then(res => setColumns([{ parentId: null, items: res?.data ?? [] }]))
                .catch(() => setTreeError(true));

            getPointDependents([point.id])
                .then(res => setDependentCount(res?.count ?? 0))
                .catch(() => setDependentCount(null));
        }
        wasOpen.current = open;
    }, [open, point, fetchComponentRoots, getPointDependents]);

    useEffect(() => {
        const c = scrollRef.current;
        if (c) c.scrollLeft = c.scrollWidth;
    }, [columns.length, loadingColumn]);

    const handleSelect = (item, columnIndex) => {
        setError(null);
        setTarget(item);
        setPath(prev => [...prev.slice(0, columnIndex), item]);

        if (!item.has_dependents) {
            setColumns(prev => prev.slice(0, columnIndex + 1));
            setLoadingColumn(null);
            return;
        }

        const token = (requestToken.current += 1);
        setColumns(prev => prev.slice(0, columnIndex + 1));
        setLoadingColumn(columnIndex + 1);
        fetchComponentChildren(item.id)
            .then(res => {
                if (token !== requestToken.current) return;
                setColumns(prev => [
                    ...prev.slice(0, columnIndex + 1),
                    { parentId: item.id, items: res?.data ?? [] },
                ]);
                setLoadingColumn(null);
            })
            .catch(() => {
                if (token !== requestToken.current) return;
                setLoadingColumn(null);
                setTreeError(true);
            });
    };

    const isCurrent = target && target.id === currentComponentId;
    const canMove = target && !isCurrent && !moving;

    const handleMove = async () => {
        if (!canMove) return;
        setMoving(true);
        setError(null);
        try {
            const res = await moveComponentPoint(point.id, target.id);
            if (!res) throw new Error('Failed to move the point. Please try again.');
            await onMoved();
            setOpen(false);
        } catch (err) {
            const raw =
                err?.response?.data?.message ||
                err?.response?.data ||
                err?.message;
            setError(
                typeof raw === 'string' && raw.trim()
                    ? raw
                    : 'Failed to move the point. Please try again.'
            );
        } finally {
            setMoving(false);
        }
    };

    const close = () => {
        if (moving) return;
        setOpen(false);
    };

    return (
        <Modal open={open} onClose={close} size="large" style={{ textAlign: 'left' }}>
            <Modal.Header>Move point</Modal.Header>
            <Modal.Content>
                <p style={{ color: '#6b7c85' }}>
                    Choose the component to move this point to. Its recorded equipment data follows
                    the point — no history is lost.
                    {dependentCount != null && dependentCount > 0 && (
                        <>
                            {' '}
                            <strong>
                                {dependentCount} piece{dependentCount === 1 ? '' : 's'} of equipment
                            </strong>{' '}
                            will move with it.
                        </>
                    )}
                </p>

                {treeError ? (
                    <Message negative size="small" content="Failed to load components." />
                ) : (
                    <div
                        ref={scrollRef}
                        className="component-manager-columns"
                        style={{
                            border: '1px solid #d4d4d5',
                            borderRadius: '4px',
                            minHeight: '200px',
                            maxHeight: '46vh',
                        }}
                    >
                        {columns.map((column, columnIndex) => {
                            const selectedInColumn = path[columnIndex];
                            const sortedItems = [...column.items].sort((a, b) =>
                                (a.num ?? '').localeCompare(b.num ?? '', undefined, {
                                    numeric: true,
                                    sensitivity: 'base',
                                }) ||
                                componentLabel(a).localeCompare(componentLabel(b), undefined, {
                                    numeric: true,
                                    sensitivity: 'base',
                                })
                            );
                            return (
                                <div
                                    className="component-manager-column"
                                    key={`move-column-${columnIndex}-${column.parentId ?? 'root'}`}
                                >
                                    {sortedItems.length === 0 ? (
                                        <div className="component-manager-empty">
                                            {columnIndex === 0 ? 'No components.' : 'No child components.'}
                                        </div>
                                    ) : (
                                        sortedItems.map(item => {
                                            const isActive =
                                                selectedInColumn && selectedInColumn.id === item.id;
                                            const isHere = item.id === currentComponentId;
                                            return (
                                                <button
                                                    type="button"
                                                    key={item.id}
                                                    className={`component-manager-item${
                                                        isActive ? ' active' : ''
                                                    }`}
                                                    onClick={() => handleSelect(item, columnIndex)}
                                                    aria-current={isActive ? 'true' : undefined}
                                                    data-testid={`move-item-${item.id}`}
                                                >
                                                    <span className="component-manager-item-label">
                                                        {componentLabel(item)}
                                                        {isHere && (
                                                            <span style={{ color: '#8c979c' }}>
                                                                {' '}
                                                                (current)
                                                            </span>
                                                        )}
                                                    </span>
                                                    {item.has_dependents ? (
                                                        <span
                                                            className="component-manager-chevron"
                                                            aria-hidden="true"
                                                        >
                                                            ›
                                                        </span>
                                                    ) : null}
                                                </button>
                                            );
                                        })
                                    )}
                                </div>
                            );
                        })}
                        {loadingColumn !== null && loadingColumn === columns.length ? (
                            <div className="component-manager-column">
                                <div className="component-manager-column-loading">
                                    <Loader active inline size="small" />
                                </div>
                            </div>
                        ) : null}
                    </div>
                )}

                <div style={{ marginTop: '0.75rem', minHeight: '1.5rem' }}>
                    {target ? (
                        isCurrent ? (
                            <span style={{ color: '#8c979c' }}>
                                This is the point's current component.
                            </span>
                        ) : (
                            <span>
                                Move to: <strong>{componentLabel(target)}</strong>
                            </span>
                        )
                    ) : (
                        <span style={{ color: '#8c979c' }}>No component selected.</span>
                    )}
                </div>

                {error && (
                    <div style={{ marginTop: '0.5rem' }}>
                        <Message error size="small" content={error} />
                    </div>
                )}
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={close} disabled={moving} data-testid="move-cancel">
                    Cancel
                </Button>
                <Button
                    className="ui primary button"
                    onClick={handleMove}
                    loading={moving}
                    disabled={!canMove}
                    data-testid="move-confirm"
                >
                    Move
                </Button>
            </Modal.Actions>
        </Modal>
    );
};
